import useCanvasStore from '../components/pages/Canvas/useCanvasStore';
import useProjectStore from '../components/ProjectCanvas/useProjectStore';
import { getCurrentTime } from './getCurrentTime';
import { send_component_save_request } from './Request';
import { storeMap } from './prefillComponentStores';
import { validateSavePayload } from './storeWriteGuard';
import { normalizeV1ToV2 } from './enumNormalization';

/**
 * Reads the component name from the keyed store.
 * All stores now use the standard 'name' field.
 */
function resolveComponentName(ks, fallback) {
  if (!ks) return fallback;
  if (ks.name) return ks.name;
  return fallback;
}

/**
 * Builds a standardized save payload for component-service POST requests.
 * All component data (name, description, metadata, template config) flows
 * through this single payload shape.
 *
 * @param {string} componentId
 * @param {string} type - component type (e.g. 'email', 'ai_assistant')
 * @param {object} templateData - template-specific config from the keyed store
 * @param {object} [overrides] - optional field overrides (e.g. status from AI builder)
 * @param {object} [keyedStore] - optional keyed Zustand store; name/description read from here
 * @returns {object} payload ready for send_component_save_request
 */
export function buildComponentSavePayload(componentId, type, templateData, overrides = {}, keyedStore = null) {
  const ps = useProjectStore.getState();
  const ks = keyedStore ? keyedStore.getState() : null;

  // Resolve name: keyed store (generic or type-specific) -> global project store
  const name = resolveComponentName(ks, ps.componentName);

  const normalizedData = normalizeV1ToV2(type, templateData);

  return {
    componentData: {
      schemaVersion: 2,
      ...normalizedData,
      name,
      lastModified: getCurrentTime(),
    },
    componentId,
    type,
    description: ks?.componentDescription ?? ps.componentDescription,
    status: null,
    metadataEnabled: ps.metadataEnabled,
    startTimestampLabel: ps.startTimestampLabel,
    endTimestampLabel: ps.endTimestampLabel,
    metadataTimestampFormat: ps.metadataTimestampFormat,
    ...overrides,
  };
}

/**
 * Injects the current metadata fields from useProjectStore into an existing
 * save payload object. Use this for templates with non-standard payload shapes
 * that can't use buildComponentSavePayload directly.
 */
export function injectMetadataFields(payload) {
  const ps = useProjectStore.getState();
  payload.metadataEnabled = ps.metadataEnabled;
  payload.startTimestampLabel = ps.startTimestampLabel;
  payload.endTimestampLabel = ps.endTimestampLabel;
  payload.metadataTimestampFormat = ps.metadataTimestampFormat;
  return payload;
}

/**
 * Saves a component directly by reading its keyed store and POSTing to
 * component-service. Eliminates the saveTriggerValue pub/sub chain.
 *
 * Works even after the template component has unmounted because the keyed
 * store persists in the componentStoreRegistry.
 *
 * SAFETY: This function runs through multiple validation gates before
 * sending any data to the backend. It will REFUSE to save if:
 *   - The store was never loaded with real data (loaded === false)
 *   - The template data is null, empty, or missing
 *   - The payload looks like a regression from the prefill snapshot
 *     (data shrunk by 70%+, or all values became empty)
 *
 * @param {string} componentType - e.g. 'email', 'code', 'calculator'
 * @param {string} componentId
 * @param {object} [overrides] - optional overrides (status, sessionId, etc.)
 * @returns {Promise} resolves with the HTTP response, or undefined if blocked
 */
export async function saveComponentByType(componentType, componentId, overrides = {}) {
  const config = storeMap[componentType];
  if (!config) return;

  const store = config.getStore(componentId);
  const state = store.getState();

  if (!state.loaded) {
    console.error(
      `[SAVE BLOCKED] ${componentType}/${componentId}: store.loaded is false — refusing to save empty defaults`
    );
    return { _blocked: true };
  }

  // Stores with custom save() handle their own validation
  if (typeof state.save === 'function') {
    return state.save(overrides);
  }

  const endpoint = state.endpoint;
  if (!endpoint) return;

  const template = state.getTemplate();
  const templateData = template.data || template.componentData;

  // ═══════════════════════════════════════════════════════════════════════
  // GATE 2: Validate the payload through storeWriteGuard.
  // This checks: non-null, non-empty, not a regression from the prefill
  // snapshot, and not all-default values. If ANY check fails, the save
  // is blocked and an error is logged.
  // ═══════════════════════════════════════════════════════════════════════
  if (!validateSavePayload(componentType, componentId, templateData, store)) {
    return { _blocked: true };
  }

  const payload = buildComponentSavePayload(
    componentId,
    componentType,
    templateData,
    overrides,
    store,
  );

  // ═══════════════════════════════════════════════════════════════════════
  // GATE 3: Final sanity check on the assembled payload.
  // The componentData.data in the payload must exist and be non-empty.
  // ═══════════════════════════════════════════════════════════════════════
  if (!payload.componentData?.schemaVersion) {
    console.error(
      `[SAVE BLOCKED] ${componentType}/${componentId}: assembled payload has no componentData.schemaVersion`
    );
    return { _blocked: true };
  }

  const res = await send_component_save_request(
    `component-service/${endpoint}/data`,
    payload,
    '',
    'POST',
  );

  // Update rawComponentData in the project store so inline previews and
  // downstream placeholder computations stay in sync without a page refresh.
  try {
    const comps = useProjectStore.getState().components;
    if (comps) {
      const idx = comps.findIndex(c => c.componentId === componentId);
      const updated = [...comps];
      if (idx !== -1) {
        updated[idx] = { ...updated[idx], rawComponentData: payload.componentData };
      } else {
        updated.push({
          componentId,
          type: componentType,
          rawComponentData: payload.componentData,
          nextComponents: [],
        });
      }
      useProjectStore.setState({ components: updated });
    }
  } catch (e) {
    console.warn('[saveComponentByType] failed to update rawComponentData in store:', e);
  }

  // Sync schemaVersion into the canvas node so the v1/v2 chip updates
  // immediately without requiring a page reload.
  try {
    const sv = payload.componentData?.schemaVersion;
    if (sv != null) {
      useCanvasStore.getState().setNodesFn(nodes =>
        nodes.map(n =>
          n.id === componentId && n.data?.schemaVersion !== sv
            ? { ...n, data: { ...n.data, schemaVersion: sv } }
            : n
        )
      );
    }
  } catch (e) {
    console.warn('[saveComponentByType] failed to sync schemaVersion to canvas node:', e);
  }

  return res;
}
